import { cva } from 'class-variance-authority';
import clsx from 'clsx';

import { getChannelIconName } from '@/utils/channel-icon';

import { Typography } from './typography';

const channelIconVariants = cva('h-6', {
  variants: {
    variant: {
      small: 'w-6',
      large: 'w-auto',
    },
    defaultVariants: {
      variant: 'small',
    },
  },
});

interface ChannelIconProps {
  channelSlug: string;
  channelName?: string;
  variant?: 'small' | 'large';
  className?: string;
}

export function ChannelIcon({
  channelSlug,
  channelName,
  variant = 'small',
  className,
}: ChannelIconProps) {
  const channelIcons: Record<string, { default: string }> = import.meta.glob(
    '@/assets/icons/channels/*/*.svg',
    { eager: true },
  );

  const getIconSrc = (channel: string, iconVariant: 'small' | 'large') =>
    channelIcons[
      `/src/assets/icons/channels/${iconVariant}/${getChannelIconName(
        channel.toLowerCase(),
      )}.svg`
    ]?.default;

  if (!channelSlug) return null;

  const largeIconSrc = getIconSrc(channelSlug, 'large');
  const smallIconSrc = getIconSrc(channelSlug, 'small');

  // If large variant is requested but not available, fall back to small icon with channel name
  if (variant === 'large' && !largeIconSrc && smallIconSrc && channelName) {
    return (
      <div className={clsx('flex items-center gap-2', className)}>
        <img
          key={channelSlug}
          src={smallIconSrc}
          alt={channelName ?? 'Channel'}
          className={channelIconVariants({ variant, className })}
        />
        <Typography variant="large2">{channelName}</Typography>
      </div>
    );
  }

  const iconSrc = variant === 'large' ? largeIconSrc : smallIconSrc;

  if (!iconSrc) return null;

  return (
    <img
      key={channelSlug}
      src={iconSrc}
      alt={channelName ?? 'Channel'}
      className={clsx(channelIconVariants({ variant, className }))}
    />
  );
}
